import React, { useState } from "react";
import Footer from "../../components/Footer/Footer";
import LandingHeader from "../../components/LandingHeader/LandingHeader";
import { useLanguage } from "../../contexts/LanguageContext";

const helpTranslations = {
  en: {
    title: 'Help & Support',
    subtitle: 'Find answers to common questions and get the support you need.',
    gettingStarted: 'Getting Started',
    accountHelp: 'Account & Login',
    featuresHelp: 'Features & Usage',
    troubleshooting: 'Troubleshooting',
    contactUs: 'Contact Us',
    faq: [
      {
        category: 'gettingStarted',
        question: 'How do I create an account?',
        answer: 'To create an account, click the "Join the Waitlist" button on our homepage. You\'ll be asked to provide your email address and some basic information. Once you\'re on the waitlist, you\'ll receive an invitation when we launch!'
      },
      {
        category: 'gettingStarted',
        question: 'What is Welby?',
        answer: 'Welby is your AI health companion within the UME app. Welby helps simplify complex healthcare information, provides personalized reminders, and guides you through your care journey with clear, actionable steps.'
      },
      {
        category: 'accountHelp',
        question: 'How do I reset my password?',
        answer: 'If you\'ve forgotten your password, click the "Forgot Password" link on the login page. Enter your email address and we\'ll send you instructions to reset your password.'
      },
      {
        category: 'accountHelp',
        question: 'How do I update my profile information?',
        answer: 'Once logged in, go to Settings > Profile to update your personal information, contact details, and preferences.'
      },
      {
        category: 'featuresHelp',
        question: 'How do I upload medical documents?',
        answer: 'In the app, navigate to your Medical History section. Click "Upload" and select the documents you want to add. Welby will help organize and interpret the information for you.'
      },
      {
        category: 'featuresHelp',
        question: 'Can I share access with family members?',
        answer: 'Yes! In Settings > Sharing, you can grant secure access to trusted family members or caregivers. You control what information they can see and can revoke access anytime.'
      },
      {
        category: 'troubleshooting',
        question: 'The app is running slowly. What can I do?',
        answer: 'Try clearing your browser cache and cookies, or restart the app. If the issue persists, check your internet connection or try using a different browser.'
      },
      {
        category: 'troubleshooting',
        question: 'I\'m having trouble uploading files. What file types are supported?',
        answer: 'We support PDF, JPG, PNG, and DOC files up to 10MB each. If you\'re having issues, try reducing the file size or converting to PDF format.'
      }
    ],
    stillNeedHelp: 'Still need help?',
    contactText: 'Can\'t find what you\'re looking for? Our support team is here to help.',
    emailUs: 'Email Us',
    supportEmail: 'support@umehealth.com',
    responseTime: 'We typically respond within 24 hours.',
    ctaTitle: 'Ready to Experience Welby?',
    ctaSubtitle: 'Join our waitlist to be among the first to try Welby, your AI health companion.',
    ctaWelbyNote: '💬 Welby can respond to any health-related questions you have - from understanding medical terms to scheduling appointments. Ask away!',
    ctaEmail: 'Email Address',
    ctaPhone: 'Phone Number (Optional)',
    ctaMessage: 'Message (Optional)',
    ctaJoinWaitlist: 'Join Waitlist',
    ctaSuccess: 'Thank you! We\'ll be in touch soon.',
    ctaPlaceholder: {
      email: 'your@email.com',
      phone: '(555) 123-4567',
      message: 'Tell us about your healthcare needs...'
    }
  },
  es: {
    title: 'Ayuda y Soporte',
    subtitle: 'Encuentra respuestas a preguntas comunes y obtén el soporte que necesitas.',
    gettingStarted: 'Primeros Pasos',
    accountHelp: 'Cuenta e Inicio de Sesión',
    featuresHelp: 'Características y Uso',
    troubleshooting: 'Solución de Problemas',
    contactUs: 'Contáctanos',
    faq: [
      {
        category: 'gettingStarted',
        question: '¿Cómo creo una cuenta?',
        answer: 'Para crear una cuenta, haz clic en el botón "Únete a la lista de espera" en nuestra página principal. Se te pedirá que proporciones tu dirección de correo electrónico y alguna información básica. ¡Una vez que estés en la lista de espera, recibirás una invitación cuando lancemos!'
      },
      {
        category: 'gettingStarted',
        question: '¿Qué es Welby?',
        answer: 'Welby es tu compañero de salud con IA dentro de la aplicación UME. Welby ayuda a simplificar información médica compleja, proporciona recordatorios personalizados y te guía a través de tu viaje de atención con pasos claros y accionables.'
      },
      {
        category: 'accountHelp',
        question: '¿Cómo restablezco mi contraseña?',
        answer: 'Si has olvidado tu contraseña, haz clic en el enlace "Olvidé mi contraseña" en la página de inicio de sesión. Ingresa tu dirección de correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.'
      },
      {
        category: 'accountHelp',
        question: '¿Cómo actualizo la información de mi perfil?',
        answer: 'Una vez que hayas iniciado sesión, ve a Configuración > Perfil para actualizar tu información personal, detalles de contacto y preferencias.'
      },
      {
        category: 'featuresHelp',
        question: '¿Cómo subo documentos médicos?',
        answer: 'En la aplicación, navega a tu sección de Historial Médico. Haz clic en "Subir" y selecciona los documentos que deseas agregar. Welby te ayudará a organizar e interpretar la información.'
      },
      {
        category: 'featuresHelp',
        question: '¿Puedo compartir acceso con miembros de la familia?',
        answer: '¡Sí! En Configuración > Compartir, puedes otorgar acceso seguro a miembros de la familia o cuidadores de confianza. Tú controlas qué información pueden ver y puedes revocar el acceso en cualquier momento.'
      },
      {
        category: 'troubleshooting',
        question: 'La aplicación se ejecuta lentamente. ¿Qué puedo hacer?',
        answer: 'Intenta borrar la caché y las cookies de tu navegador, o reinicia la aplicación. Si el problema persiste, verifica tu conexión a internet o intenta usar un navegador diferente.'
      },
      {
        category: 'troubleshooting',
        question: 'Tengo problemas para subir archivos. ¿Qué tipos de archivo son compatibles?',
        answer: 'Aceptamos archivos PDF, JPG, PNG y DOC de hasta 10MB cada uno. Si tienes problemas, intenta reducir el tamaño del archivo o convertirlo a formato PDF.'
      }
    ],
    stillNeedHelp: '¿Aún necesitas ayuda?',
    contactText: '¿No encuentras lo que buscas? Nuestro equipo de soporte está aquí para ayudar.',
    emailUs: 'Envíanos un Correo',
    supportEmail: 'support@umehealth.com',
    responseTime: 'Típicamente respondemos dentro de 24 horas.',
    ctaTitle: '¿Listo para Experimentar Welby?',
    ctaSubtitle: 'Únete a nuestra lista de espera para ser uno de los primeros en probar Welby, tu compañero de salud IA.',
    ctaWelbyNote: '💬 Welby puede responder cualquier pregunta relacionada con la salud que tengas - desde entender términos médicos hasta programar citas. ¡Pregunta sin miedo!',
    ctaEmail: 'Dirección de Correo Electrónico',
    ctaPhone: 'Número de Teléfono (Opcional)',
    ctaMessage: 'Mensaje (Opcional)',
    ctaJoinWaitlist: 'Unirse a la Lista de Espera',
    ctaSuccess: '¡Gracias! Nos pondremos en contacto pronto.',
    ctaPlaceholder: {
      email: 'tu@email.com',
      phone: '(555) 123-4567',
      message: 'Cuéntanos sobre tus necesidades de atención médica...'
    }
  },
  tl: {
    title: 'Tulong at Suporta',
    subtitle: 'Hanapin ang mga sagot sa mga karaniwang tanong at kunin ang suporta na kailangan mo.',
    gettingStarted: 'Mga Unang Hakbang',
    accountHelp: 'Account at Login',
    featuresHelp: 'Mga Features at Paggamit',
    troubleshooting: 'Pag-aayos ng Problema',
    contactUs: 'Makipag-ugnayan sa Amin',
    faq: [
      {
        category: 'gettingStarted',
        question: 'Paano ako gagawa ng account?',
        answer: 'Upang gumawa ng account, i-click ang button na "Sumali sa Waitlist" sa aming homepage. Hihilingin sa iyo na ibigay ang iyong email address at ilang basic na impormasyon. Kapag ikaw ay nasa waitlist, makakatanggap ka ng invitation kapag kami ay nag-launch!'
      },
      {
        category: 'gettingStarted',
        question: 'Ano ang Welby?',
        answer: 'Ang Welby ay ang iyong AI health companion sa loob ng UME app. Tinutulungan ng Welby na pasimplehin ang kumplikadong healthcare information, nagbibigay ng personalized na paalala, at ginagabayan ka sa iyong care journey sa malinaw na mga hakbang.'
      },
      {
        category: 'accountHelp',
        question: 'Paano ko i-reset ang aking password?',
        answer: 'Kung nakalimutan mo ang iyong password, i-click ang "Forgot Password" link sa login page. Ilagay ang iyong email address at kami ay magpapadala ng instructions upang i-reset ang iyong password.'
      },
      {
        category: 'accountHelp',
        question: 'Paano ko i-update ang impormasyon ng aking profile?',
        answer: 'Kapag naka-login, pumunta sa Settings > Profile upang i-update ang iyong personal na impormasyon, contact details, at preferences.'
      },
      {
        category: 'featuresHelp',
        question: 'Paano ko i-upload ang mga medical documents?',
        answer: 'Sa app, mag-navigate sa iyong Medical History section. I-click ang "Upload" at piliin ang mga dokumento na gusto mong idagdag. Tutulungan ka ng Welby na ayusin at bigyang-kahulugan ang impormasyon.'
      },
      {
        category: 'featuresHelp',
        question: 'Maaari ko bang ibahagi ang access sa mga miyembro ng pamilya?',
        answer: 'Oo! Sa Settings > Sharing, maaari kang magbigay ng secure na access sa mga tiwala na miyembro ng pamilya o caregivers. Ikaw ang kumokontrol kung ano ang maaari nilang makita at maaari mong bawiin ang access anumang oras.'
      },
      {
        category: 'troubleshooting',
        question: 'Ang app ay tumatakbo nang mabagal. Ano ang maaari kong gawin?',
        answer: 'Subukang burahin ang cache at cookies ng iyong browser, o i-restart ang app. Kung ang problema ay magpatuloy, suriin ang iyong internet connection o subukang gumamit ng ibang browser.'
      },
      {
        category: 'troubleshooting',
        question: 'May problema ako sa pag-upload ng mga file. Ano ang mga file types na sinusuportahan?',
        answer: 'Sinusuportahan namin ang PDF, JPG, PNG, at DOC files na hanggang 10MB kada isa. Kung mayroon kang mga issues, subukang bawasan ang file size o i-convert sa PDF format.'
      }
    ],
    stillNeedHelp: 'Kailangan pa rin ng tulong?',
    contactText: 'Hindi mahanap ang hinahanap mo? Ang aming support team ay narito upang tumulong.',
    emailUs: 'I-email Kami',
    supportEmail: 'support@umehealth.com',
    responseTime: 'Karamihan ay sumasagot kami sa loob ng 24 oras.',
    ctaTitle: 'Handa Ka Bang Subukan si Welby?',
    ctaSubtitle: 'Sumali sa aming waitlist upang maging isa sa mga unang susubok sa Welby, ang iyong AI health companion.',
    ctaWelbyNote: '💬 Maaaring sumagot si Welby sa anumang tanong tungkol sa kalusugan na mayroon ka - mula sa pag-unawa sa mga terminong medikal hanggang sa pag-iskedyul ng mga appointment. Tanungin mo!',
    ctaEmail: 'Email Address',
    ctaPhone: 'Numero ng Telepono (Opsyonal)',
    ctaMessage: 'Mensahe (Opsyonal)',
    ctaJoinWaitlist: 'Sumali sa Waitlist',
    ctaSuccess: 'Salamat! Makakausap ka namin sa lalong madaling panahon.',
    ctaPlaceholder: {
      email: 'iyong@email.com',
      phone: '(555) 123-4567',
      message: 'Sabihin sa amin ang tungkol sa iyong mga pangangailangan sa kalusugan...'
    }
  }
};

const HelpSupportPage = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const t = (key) => {
    return helpTranslations[language][key] || helpTranslations.en[key];
  };

  const getCategoryTitle = (category) => {
    return t(category);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    console.log('CTA Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ email: '', phone: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <LandingHeader />

      <main className="flex-1 px-4 py-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">{t('title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('subtitle')}</p>
        </div>

        <div className="space-y-12">
          {/* FAQ Sections */}
          {['gettingStarted', 'accountHelp', 'featuresHelp', 'troubleshooting'].map((category) => (
            <section key={category} className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">{getCategoryTitle(category)}</h2>
              <div className="space-y-4">
                {t('faq').filter(item => item.category === category).map((item, index) => (
                  <details key={index} className="bg-white rounded-lg p-6 shadow-sm">
                    <summary className="font-medium text-gray-800 cursor-pointer hover:text-blue-600">
                      {item.question}
                    </summary>
                    <p className="mt-4 text-gray-700 leading-relaxed">{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}

          {/* Contact Section */}
          <section className="bg-blue-50 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('stillNeedHelp')}</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">{t('contactText')}</p>
            <div className="bg-white rounded-lg p-6 shadow-sm inline-block">
              <h3 className="font-semibold text-gray-800 mb-2">{t('emailUs')}</h3>
              <a href={`mailto:${t('supportEmail')}`} className="text-blue-600 hover:text-blue-800 font-medium">
                {t('supportEmail')}
              </a>
              <p className="text-sm text-gray-600 mt-2">{t('responseTime')}</p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-mint rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('ctaTitle')}</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">{t('ctaSubtitle')}</p>
            
            <div className="bg-white rounded-lg p-4 mb-6 max-w-md mx-auto shadow-sm">
              <p className="text-sm font-medium text-gray-800">{t('ctaWelbyNote')}</p>
            </div>

            <form className="max-w-md mx-auto space-y-4" onSubmit={handleSubmit}>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('ctaPlaceholder').email}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-deep-navy focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('ctaPlaceholder').phone}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-deep-navy focus:border-transparent transition-all"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('ctaPlaceholder').message}
                  rows="3"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-deep-navy focus:border-transparent transition-all resize-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitted}
                className="w-full bg-deep-navy text-white py-3 px-6 rounded-lg font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitted ? t('ctaSuccess') : t('ctaJoinWaitlist')}
              </button>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpSupportPage;
